import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getDossier } from "../../api/dossier/getDossier";
import { getDossierStatus } from "../../api/dossier/getDossierStatus";
import AutoPilotAlert from "../../components/Alert";
import APModal from "../../components/APModal";
import APButton from "../../components/buttons/APButton";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import HomeButton from "../../components/buttons/HomeButton";
import PageContainer from "../../components/PageContainer";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import { useAuth } from "../../providers/AuthProvider";
import { buildNextActionRoute, DossierPhaseEvent } from "../../types/Dossier";
import DossierStatusStepper from "../components/DossierStatusStepper";

type EventInfo = {
  title: string;
  description: string;
};

const ActionConfirmation = () => {
  const navigate = useNavigate();
  const { dossierId, eventId } = useParams();
  const [searchParams] = useSearchParams();
  const { getScopedPageUrl } = useAuth();
  const [eventDetails, setEventDetails] = useState(null as EventInfo | null);
  const [showNextActionModal, setShowNextActionModal] = useState(false);

  if (!dossierId || !eventId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id or next action param are misisng");
  }

  const nextActionIsHome =
    !!searchParams.get("nextActionIsHome") &&
    searchParams.get("nextActionIsHome") === "true";

  const dossier = getDossier(dossierId);

  const dossierStatus = getDossierStatus(dossierId);

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierStatus.isError) {
    throw dossierStatus.error;
  }

  const isLoading = dossier.isLoading || dossierStatus.isLoading;
  const isSuccess = dossier.isSuccess && dossierStatus.isSuccess;

  // Determine whether the user should see this confirmation or if they should be
  // redirected elsewhere
  useEffect(() => {
    if (isSuccess) {
      const events = dossierStatus.data.dossierPhases.reduce(
        (acc: DossierPhaseEvent[], phase) => {
          return acc.concat(phase.events);
        },
        []
      );
      const selectedEvent = events.find(
        (event) => `${event.eventId}` === eventId
      );

      if (
        selectedEvent /* TODO: fix timing issue && selectedEvent.completed*/
      ) {
        setEventDetails({
          title: selectedEvent.completionTitle || selectedEvent.eventName,
          description: selectedEvent.completionText,
        });
      } else if (eventId === "not-ready") {
        setEventDetails({
          title: "Well Done!",
          description:
            "You have completed everything we need at this stage, and we’re busy making things happen in the background.<br/><br/>We will be in contact with next steps.<br/>Want to speak to us sooner? Call us.",
        });
      } else {
        // If the event id does not match any event or the event has not been completed,
        // then we redirect to home
        navigate(getScopedPageUrl("viewDossiers"), {
          replace: true,
        });
      }
    }
  }, [isSuccess]);

  const nextAction = isSuccess ? dossierStatus.data.nextDriverAction : null;
  const nextIsHome =
    nextActionIsHome ||
    !nextAction ||
    nextAction === "no-action-required" ||
    eventId === "not-ready";

  const onNext = async () => {
    const route = nextIsHome
      ? getScopedPageUrl("viewDossiers")
      : buildNextActionRoute(dossierId, nextAction, true);

    navigate(route);
  };

  const onStatusItemClick = () => {
    setShowNextActionModal(true);
  };

  const onCloseNextActionModal = () => {
    setShowNextActionModal(false);
  };

  return (
    <PageContainer
      title={eventDetails ? eventDetails.title : ""}
      loading={isLoading || !eventDetails}
    >
      {isSuccess && eventDetails && (
        <Stack spacing={2}>
          <Typography variant="titleMedium">
            <div
              dangerouslySetInnerHTML={{ __html: eventDetails.description }}
            />
          </Typography>

          <Box>
            <Box sx={{ ml: 1, my: 1 }}>
              <DossierStatusStepper
                dossierStatus={dossierStatus.data}
                onItemClick={onStatusItemClick}
              ></DossierStatusStepper>
            </Box>

            <APModal
              isOpen={showNextActionModal}
              onClose={onCloseNextActionModal}
              confirmButton={
                <APButton
                  size="large"
                  color="secondary"
                  variant="contained"
                  text="Next"
                  onClick={() => onNext()}
                  icon="play"
                />
              }
            >
              {dossier.data.noteToDriver && (
                <Box>
                  <AutoPilotAlert
                    severity="info"
                    message={dossier.data.noteToDriver}
                    asHTML={true}
                  />
                </Box>
              )}
            </APModal>
          </Box>

          {dossier.data.noteToDriver && (
            <Box>
              <AutoPilotAlert
                severity="info"
                message={dossier.data.noteToDriver}
                asHTML={true}
              />
            </Box>
          )}

          <Box
            sx={{
              pt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <RestrictedAppScope scope="external">
                {nextIsHome && (
                  <APContactHelpButton
                    regardingOpportunityId={dossier.data.opportunity?.id}
                  />
                )}
              </RestrictedAppScope>
            </Box>
            {nextIsHome ? (
              <HomeButton destination="dossierHome" dossierId={dossierId} />
            ) : (
              <APButton
                text="Next"
                variant="contained"
                color="secondary"
                onClick={() => onNext()}
                icon="play"
              />
            )}
          </Box>
        </Stack>
      )}
    </PageContainer>
  );
};

export default ActionConfirmation;
